import * as React from 'react'
import { useContext, useEffect, useLayoutEffect, useState, useRef } from 'react'
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
  Route,
  Navigate,
  Routes,
  createSearchParams,
} from 'react-router-dom'
import { store, StateProvider } from './store'
import { KaseTheme } from './theme/KaseTheme'
import KaseToastContainer from './containers/KaseToastContainer'
import { CrumbRoute } from './containers/CrumbRoute'
import { BreadcrumbsProvider } from './components/breadcrumbs/BreadcrumbsContext'

import 'react-medium-image-zoom/dist/styles.css'
import 'bootstrap/dist/css/bootstrap.css'
import { PostPage } from './pages/login/styles'

// Components
import { notify } from './components/component-items/helpers'
import { Chrome, Background, Border } from './components/chrome/chrome'

// Global
import LoginPage from './pages/login'
import { NotFoundPage } from './pages/error'
import { BadRequestPage } from './pages/error/bad-request'
import { AccessDenied, ProtectedRoute } from './components/rbac'
import { WarehouseDashboard, MerchantDashboard } from './pages/dashboard'

// Orders
import { OrderList } from './pages/orders'
import { CreateOrder } from './pages/orders/new'
import { UploadOrders } from './pages/orders/upload'
import { OrderPage } from './pages/orders/order'

//ASNs
import { AsnList } from './pages/asns'
import { CreateAsn } from './pages/asns/new'
import { AsnPage } from './pages/asns/asn'
import { SelectItem } from './pages/asns/asn/item'
import { ConfirmDetails } from './pages/asns/asn/item/confirm-details'
import { ReceiveInventory } from './pages/asns/asn/item/receive-inventory'
import { ReceivingReview } from './pages/asns/asn/item/receiving-review'
import { ReturnList } from './pages/returns'

// Products
import { ProductList } from './pages/products'
import { CreateProduct } from './pages/products/new'
import { ProductPage } from './pages/products/product'
import { PromoRules } from './pages/products/rules'
import { ProductVariantPage } from './pages/products/product/variant'
import { PackageList } from './pages/packages'
import { CreatePackaging } from './pages/packages/new'
import { PackagingRules } from './pages/packages/rules'

// Postage
import { PostageReconcile } from './pages/postage/reconcile'
import { RateCalculator } from './pages/postage/rate-calculator'
import { PostageManifests } from './pages/postage/manifests'

// Projects
import { Project } from './pages/projects/project'
import { ProjectList } from './pages/projects'

// Locations
import { LocationList } from './pages/locations'
import { LocationEdit } from './pages/locations/edit'
import { LocationView } from './pages/locations/location'

// Company Pages
import { Companies } from './pages/companies'
import { CompanyUsers } from './pages/company/users'
import { Company } from './pages/company'
import { PackingSlip } from './pages/company/packing-slip'
import { ShippingMethods } from './pages/company/shipping-methods'
import { EmailNotificationList } from './pages/company/email-notifications'
import { Reports } from './pages/company/reports'
import { FulfillmentPoliciesPage } from './pages/orders/fulfillment-policies'

// Billing Pages
import { BillingPage } from './pages/billing/header'
import { CommissionsReport } from './pages/billing/commissions'
import { PricingTier } from './pages/billing/pricing-tier'
import { PricingTool } from './pages/billing/pricing-tool'

// Inventory Pages
import { InventoryList } from './pages/inventory'
import { StockAlertsPage } from './pages/company/stock-alerts'
import { RunDownRatePage } from './pages/inventory/run-down-rate'
import { MoveInventoryPage } from './pages/inventory/move-inventory'
import { ContainerList } from './pages/inventory/containers'
import { ContainerDetail } from './pages/inventory/containers/container'
import { ContainerMovePage } from './pages/inventory/containers/move'
import { InventoryTransactionListPage } from './pages/inventory/transactions'

// Picking Workflow
import { PickingLocation } from './pages/warehouse/picking/location'
import { MatrixSlotting } from './pages/warehouse/picking/matrix-slotting'
import { BulkSlotting } from './pages/warehouse/picking/bulk-slotting'

// Packing Workflow
import { PackOrder, PrePackOrder } from './pages/warehouse/packing/pack-order'
import { PackSingleOrder } from './pages/warehouse/packing/pack-order/pack-single-order'
import { SerialNumbers } from './pages/warehouse/packing/serial-numbers'
import { ConfirmDimensions, PrePackDimensions } from './pages/warehouse/packing/confirm-dimensions'
import { LabelPurchase } from './pages/warehouse/packing/label-purchase'
import { PackingShipmentDetails } from './pages/warehouse/packing/shipment-details'

// Warehouse
import { ShipOrderPage } from './pages/ship-order-page'
import { ExitScanPage } from './pages/exit-scan'
import { WarehouseUsersPage } from './pages/warehouse/users'
import { UserPermissionPage } from './pages/warehouse/users/permissions'
import { MerchantUserPermissionPage } from './pages/company/users/permissions'
import { ReceiveASN } from './pages/asns/receive-asn'

// EDI
import { EDITransactionList } from './pages/edi'

// Automations
import { Actions } from './pages/actions/'

// Batches
import { BatchList } from './pages/warehouse/batches'
import { StartBatch } from './pages/warehouse/batches/start'
import { Batch } from './pages/warehouse/batches/batch'
import { BatchProfile } from './pages/warehouse/waves'

// Integrations
import { ShopifyIntegrationPage } from './pages/integrations/shopify'
import { LoopIntegrationPage } from './pages/integrations/loop'
import { QuickbooksIntegrationPage } from './pages/integrations/quickbooks'
import { ListWebhookPage, WebhookEventsPage } from './pages/company/webhooks'

// Core
import { AsyncTaskList } from './pages/core/tasks'

// User
import { Profile } from './pages/user/profile'

// Keyboard Shortcuts
import { connectShortcuts } from './keyboard-shortcuts'
import { NotificationPage } from './pages/notifications'

const loader = document.querySelector('.loader-container')

const hideLoader = () => loader?.classList.add('loader--hide')

const MainRoutes = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    state: { isAuthenticated, actAs, csrf, userIsWarehouse, backorderManagementEnabled },
    dispatch,
  } = useContext(store)
  const [isLoading, setLoading] = useState(true)
  const isLoadingRef = useRef(isLoading)
  isLoadingRef.current = isLoading

  let error = new URLSearchParams(location.search).get('error')
  let nextUrl = new URLSearchParams(location.search).get('next')
  let returnTo = new URLSearchParams(location.search).get('returnto')

  var searchParams = new URLSearchParams(location.search)
  searchParams.set('next', location.pathname)

  // Keyboard Shortcuts:
  // Attaches listener on a global level for keyboard shortcuts
  // such as: Start Shipping, Exit Scanning, etc
  useEffect(() => {
    if (userIsWarehouse) {
      const handleKeyDown = (event) => {
        connectShortcuts(event, null, 'global', navigate)
      }

      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [userIsWarehouse])

  useEffect(() => {
    getCSRF()
    const timer = setTimeout(() => {
      if (isLoadingRef.current) {
        window.location.reload()
      }
    }, 15000)
    return () => clearTimeout(timer)
  }, [])

  useLayoutEffect(() => {
    getSession()
    // Show toast when error is routed with URL
    if (error) {
      notify({ type: 'error', message: error })
      var errorSearchParams = new URLSearchParams(location.search)
      errorSearchParams.delete('error')
      navigate({
        pathname: location.pathname,
        search: createSearchParams(errorSearchParams).toString(),
      })
    }
  }, [])

  const getCSRF = () => {
    fetch('/api/csrf/', {
      credentials: 'include',
    })
      .then((res) => {
        let csrfToken = res.headers.get('X-CSRFToken')
        dispatch({ type: 'csrf', csrf: csrfToken })
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const getSession = async () => {
    fetch('/api/session/', {
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrf,
      },
    })
      .then((res) => res.json())
      .then(async (payload) => {
        if (payload.isAuthenticated) {
          // Fetch RBAC permissions if not included in session response
          if (!payload.rbac && payload.userID) {
            try {
              const rbacResp = await fetch(`/api/rbac/users/${payload.userID}/permissions/effective/`, {
                credentials: 'include',
                headers: {
                  'X-CSRFToken': csrf,
                },
              })
              if (rbacResp.ok) {
                const rbacData = await rbacResp.json()
                // The endpoint returns {data: {...}}, extract the inner data
                payload.rbac = rbacData.data || rbacData
              }
            } catch (e) {
              console.warn('Failed to fetch RBAC permissions:', e)
            }
          }

          // Set impersonation state from session data
          if (payload.impersonation) {
            dispatch({
              type: 'setImpersonation',
              payload: {
                is_impersonating: payload.impersonation.is_impersonating,
                can_impersonate: payload.impersonation.can_impersonate,
                impersonated_user: payload.impersonation.impersonated_user,
                original_user: payload.impersonation.original_user,
              },
            })
          }

          dispatch({ type: 'session', payload: payload })
        } else {
          throw Error(payload.detail)
        }
        setLoading(false)
        hideLoader()
      })
      .catch((err) => {
        console.log(err)
        getCSRF()
        setLoading(false)
        hideLoader()
      })
  }
  return isLoading ? null : (
    <Routes>
      <Route
        path="/login/"
        element={
          isAuthenticated ? (
            nextUrl ? (
              nextUrl.startsWith('/api') ? (
                window.location.replace(nextUrl)
              ) : (
                <Navigate to={nextUrl} />
              )
            ) : (
              <Navigate to={userIsWarehouse ? '/warehouse/dashboard/' : '/company/dashboard/'} />
            )
          ) : (
            <LoginPage />
          )
        }
      />
      <Route path="/404/" element={<NotFoundPage />} />
      <Route path="/500/" element={<BadRequestPage />} />
      <Route path="/access-denied/" element={<AccessDenied />} />
      <Route path="/" element={<Navigate to="/login/" />} />
      {isAuthenticated ? (
        <Route
          path="/*"
          element={
            <Chrome>
              <Routes>
                <Route path="core">
                  <Route
                    title="Async Tasks"
                    path="tasks"
                    element={
                      <ProtectedRoute segment="settings" minLevel="admin">
                        <CrumbRoute title="Async Tasks">
                          <AsyncTaskList />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="warehouse">
                  <Route
                    title="Companies"
                    path="companies"
                    element={
                      <CrumbRoute title="Companies">
                        {returnTo && actAs ? <Navigate to={returnTo} replace /> : <Companies />}
                      </CrumbRoute>
                    }
                  />
                  <Route
                    title="Exit Scan"
                    path="exit-scan"
                    element={
                      <ProtectedRoute segment="outbound" minLevel="operator" warehouseOnly>
                        <CrumbRoute title="Exit Scan">
                          <ExitScanPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route title="Dashboard" path="dashboard" element={<WarehouseDashboard />} />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute segment="settings" minLevel="operator">
                        <CrumbRoute title="Warehouse Users">
                          <WarehouseUsersPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/:userId/permissions"
                    element={
                      <ProtectedRoute segment="settings" minLevel="operator">
                        <CrumbRoute title="User Permissions">
                          <UserPermissionPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    title="Start Shipping"
                    path="start-shipping"
                    element={
                      <ProtectedRoute segment="outbound" minLevel="operator">
                        <CrumbRoute title="Start Shipping">
                          <ShipOrderPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    title="Start Receiving"
                    path="start-receiving"
                    element={
                      <ProtectedRoute segment="inbound" minLevel="operator">
                        <CrumbRoute title="Start Receiving">
                          <ReceiveASN />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="order/:id" element={<Navigate to="/order/:id" replace />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="order"
                  element={
                    <ProtectedRoute segment="outbound" minLevel="viewer">
                      <CrumbRoute title="Orders" path="/order" />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<OrderList />} />
                  <Route
                    path="new"
                    element={
                      <CrumbRoute companyRequired title="New">
                        <CreateOrder />
                      </CrumbRoute>
                    }
                  />
                  <Route
                    path="upload-orders"
                    element={
                      <CrumbRoute companyRequired title="Upload Orders" path="/order/upload-orders/">
                        <UploadOrders />
                      </CrumbRoute>
                    }
                  />
                  {userIsWarehouse && backorderManagementEnabled && (
                    <Route
                      path="fulfillment-policies"
                      element={
                        <ProtectedRoute segment="settings" minLevel="admin">
                          <CrumbRoute
                            companyRequired
                            title="Fulfillment Policies"
                            path="/order/fulfillment-policies/"
                          />
                        </ProtectedRoute>
                      }
                    >
                      <Route path="" element={<FulfillmentPoliciesPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  )}
                  <Route path=":id" element={<CrumbRoute title="Order Page" path="/order/:id" />}>
                    <Route index element={<OrderPage />} />
                    <Route
                      path="*"
                      element={
                        <Routes>
                          <Route
                            path="pack"
                            element={
                              <CrumbRoute title="Pack Order" path="/order/:id/pack/">
                                <PackOrder />
                              </CrumbRoute>
                            }
                          />
                          <Route
                            path="pre-pack"
                            element={
                              <CrumbRoute title="Pre-Pack Order" path="/order/:id/pre-pack/">
                                <PrePackOrder />
                              </CrumbRoute>
                            }
                          />
                          <Route
                            path="serial-numbers"
                            element={
                              <CrumbRoute title="Add Serial Numbers" path="/order/:id/serial-numbers/">
                                <SerialNumbers />
                              </CrumbRoute>
                            }
                          />
                          <Route
                            path="confirm-dimensions"
                            element={
                              <CrumbRoute title="Confirm Dimensions" path="/order/:id/confirm-dimensions/">
                                <ConfirmDimensions />
                              </CrumbRoute>
                            }
                          />
                          <Route
                            path="pre-pack-dimensions"
                            element={
                              <CrumbRoute title="Pre-Pack Dimensions" path="/order/:id/pre-pack-dimensions/">
                                <PrePackDimensions />
                              </CrumbRoute>
                            }
                          />
                          <Route
                            path="label-purchase"
                            element={
                              <CrumbRoute title="Label Purchase" path="/order/:id/label-purchase/">
                                <LabelPurchase />
                              </CrumbRoute>
                            }
                          />
                          <Route
                            path="shipment-details"
                            element={
                              <CrumbRoute title="Shipment Details" path="/order/:id/shipment-details/">
                                <PackingShipmentDetails />
                              </CrumbRoute>
                            }
                          />
                        </Routes>
                      }
                    />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="batch"
                  element={
                    <ProtectedRoute segment="outbound" minLevel="viewer">
                      <CrumbRoute title="Batches" path="/batch" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<BatchList />} />
                  <Route path="profile" element={<CrumbRoute title="Batch Profile" />}>
                    <Route path="" element={<BatchProfile />} />
                  </Route>
                  <Route
                    path=":id/*"
                    element={
                      <Routes>
                        <Route path="" element={<Batch />} />
                        <Route
                          path="start"
                          element={
                            <CrumbRoute title="Start Batch" path="/batch/:id/start/">
                              <StartBatch />
                            </CrumbRoute>
                          }
                        />
                        <Route
                          path="pack-single"
                          element={
                            <CrumbRoute title="Pack Single Item Orders" path="/batch/:id/pack-single/">
                              <PackSingleOrder />
                            </CrumbRoute>
                          }
                        />
                        <Route
                          path="pick/:inventory_item"
                          element={
                            <CrumbRoute title="Pick Location" path="/batch/:id/pick/:inventory_item/">
                              <PickingLocation />
                            </CrumbRoute>
                          }
                        />
                        <Route
                          path="pick/:inventory_item/slotting"
                          element={
                            <CrumbRoute title="Matrix Slotting" path="/batch/:id/pick/:inventory_item/slotting/">
                              <MatrixSlotting />
                            </CrumbRoute>
                          }
                        />
                        <Route
                          path="pick/:inventory_item/bulk-slotting"
                          element={
                            <CrumbRoute title="Bulk Slotting" path="/batch/:id/pick/:inventory_item/bulk-slotting/">
                              <BulkSlotting />
                            </CrumbRoute>
                          }
                        />
                      </Routes>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="asn"
                  element={
                    <ProtectedRoute segment="inbound" minLevel="viewer">
                      <CrumbRoute title="ASNs" path="/asn" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<AsnList />} />
                  <Route path="new" element={<CrumbRoute companyRequired title="New" />}>
                    <Route path="" element={<CreateAsn />} />
                  </Route>
                  <Route path=":id" element={<CrumbRoute title="ASN Details" path="/asn/:id/" />}>
                    <Route path="" element={<AsnPage />} />
                    <Route path="receive" element={<CrumbRoute title="Receive ASN" />}>
                      <Route path="" element={<SelectItem />} />
                    </Route>
                    <Route path="item">
                      <Route path=":shipnotice_item_id">
                        <Route
                          path="confirm-details"
                          element={
                            <CrumbRoute
                              title="Confirm Variant Details"
                              path="/asn/:id/item/:shipnotice_item_id/confirm-details/"
                            />
                          }
                        >
                          <Route path="" element={<ConfirmDetails />} />
                        </Route>
                        <Route
                          path="receive-inventory"
                          element={
                            <CrumbRoute
                              title="Receive Inventory"
                              path="/asn/:id/item/:shipnotice_item_id/receive-inventory/"
                            />
                          }
                        >
                          <Route path="" element={<ReceiveInventory />} />
                        </Route>
                        <Route
                          path="receiving-review"
                          element={
                            <CrumbRoute
                              title="Receiving Review"
                              path="/asn/:id/item/:shipnotice_item_id/receiving-review/"
                            />
                          }
                        >
                          <Route path="" element={<ReceivingReview />} />
                        </Route>
                        <Route path="" element={<SelectItem />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="return">
                  <Route
                    path=""
                    element={
                      <ProtectedRoute segment="inbound" minLevel="viewer">
                        <CrumbRoute title="Returns">
                          <ReturnList />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="location"
                  element={
                    <ProtectedRoute segment="inventory" minLevel="viewer">
                      <CrumbRoute title="Locations" path="/location" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<LocationList />} />
                  <Route path="edit" element={<CrumbRoute title="Batch Edit Locations" />}>
                    <Route path="" element={<LocationEdit />} />
                  </Route>
                  <Route path=":id" element={<CrumbRoute title="Location" />}>
                    <Route path="" element={<LocationView />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="product"
                  element={
                    <ProtectedRoute segment="products" minLevel="viewer">
                      <CrumbRoute title="Products" path="/product" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<ProductList />} />
                  <Route path="rule" element={<CrumbRoute companyRequired title="Promo Rules" />}>
                    <Route path="" element={<PromoRules />} />
                  </Route>
                  <Route path="new" element={<CrumbRoute companyRequired title="New Product" />}>
                    <Route path="" element={<CreateProduct />} />
                  </Route>
                  <Route path="variant/:id" element={<CrumbRoute title="Product Variant" />}>
                    <Route path="" element={<ProductVariantPage />} />
                  </Route>
                  <Route path=":id" element={<CrumbRoute title="Product Details" />}>
                    <Route path="" element={<ProductPage />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="postage">
                  <Route
                    path="reconcile"
                    element={
                      <ProtectedRoute segment="postage" minLevel="admin">
                        <CrumbRoute title="Postage Reconcile">
                          <PostageReconcile />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rate-calculator"
                    element={
                      <ProtectedRoute segment="postage" minLevel="viewer">
                        <CrumbRoute title="Rate Calculator">
                          <RateCalculator />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="manifests"
                    element={
                      <ProtectedRoute segment="postage" minLevel="viewer">
                        <CrumbRoute title="Postage Manifests">
                          <PostageManifests />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="project"
                  element={
                    <ProtectedRoute segment="vas" minLevel="viewer">
                      <CrumbRoute title="Projects" path="/project" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<ProjectList />} />
                  <Route path=":id" element={<CrumbRoute title="Project Details" />}>
                    <Route path="" element={<Project />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="edi"
                  element={
                    <ProtectedRoute segment="integrations" minLevel="viewer">
                      <CrumbRoute title="EDI Transactions">
                        <EDITransactionList />
                      </CrumbRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="edi/*" element={<NotFoundPage />} />

                <Route
                  path="action"
                  element={
                    <ProtectedRoute segment="settings" minLevel="viewer">
                      <CrumbRoute companyRequired title="Actions" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<Actions />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="package"
                  element={
                    <ProtectedRoute segment="products" minLevel="viewer">
                      <CrumbRoute title="Packaging" path="/package" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<PackageList />} />
                  <Route path="new" element={<CrumbRoute companyRequired title="New" />}>
                    <Route path="" element={<CreatePackaging />} />
                  </Route>
                  <Route path="rule" element={<CrumbRoute companyRequired title="Packaging Rules" />}>
                    <Route path="" element={<PackagingRules />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="inventory"
                  element={
                    <ProtectedRoute segment="inventory" minLevel="viewer">
                      <CrumbRoute title="Inventory" path="/inventory" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<InventoryList />} />
                  <Route
                    path="transactions"
                    element={<CrumbRoute title="Inventory Transaction List" path="/inventory/transactions/" />}
                  >
                    <Route path="" element={<InventoryTransactionListPage />} />
                  </Route>
                  <Route
                    path="stock-alerts"
                    element={
                      <ProtectedRoute segment="settings" minLevel="operator">
                        <CrumbRoute companyRequired title="Stock Alerts" />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="" element={<StockAlertsPage />} />
                  </Route>
                  <Route path="run-down-rate" element={<CrumbRoute companyRequired title="Run Down Rate" />}>
                    <Route path="" element={<RunDownRatePage />} />
                  </Route>
                  <Route path="move" element={<CrumbRoute title="Move Inventory" path="/move" />}>
                    <Route path="" element={<MoveInventoryPage />} />
                  </Route>
                  <Route path="container" element={<CrumbRoute title="Containers" />}>
                    <Route path="" element={<ContainerList />} />
                    <Route path="move" element={<CrumbRoute title="Container Move" path="/move" />}>
                      <Route path="" element={<ContainerMovePage />} />
                    </Route>
                    <Route path=":lpn" element={<CrumbRoute title="Container Detail" />}>
                      <Route path="" element={<ContainerDetail />} />
                    </Route>
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />

                <Route path="billing">
                  <Route
                    path="company"
                    element={
                      <ProtectedRoute segment="billing" minLevel="viewer">
                        <CrumbRoute companyRequired title="Billing">
                          <BillingPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="commissions"
                    element={
                      <ProtectedRoute segment="billing" minLevel="admin">
                        <CrumbRoute title="Commissions">
                          <CommissionsReport />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="pricing"
                    element={
                      <ProtectedRoute segment="billing" minLevel="operator">
                        <CrumbRoute title="Pricing Overview" path="/billing/pricing">
                          <PricingTool />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="pricing-tier"
                    element={
                      <ProtectedRoute segment="billing" minLevel="viewer">
                        <CrumbRoute title="Pricing Tier">
                          <PricingTier />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="company">
                  <Route
                    path=""
                    element={
                      userIsWarehouse && !actAs ? <Navigate to="/warehouse/dashboard/" /> : <MerchantDashboard />
                    }
                  />
                  <Route
                    path={'dashboard'}
                    element={
                      userIsWarehouse && !actAs ? <Navigate to="/warehouse/dashboard/" /> : <MerchantDashboard />
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute segment="settings" minLevel="viewer">
                        <CrumbRoute companyRequired title="Company Users">
                          <CompanyUsers />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/:userId/permissions"
                    element={
                      <ProtectedRoute segment="settings" minLevel="admin">
                        <CrumbRoute companyRequired title="User Permissions">
                          <MerchantUserPermissionPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports"
                    element={
                      <ProtectedRoute segment="reports" minLevel="viewer">
                        <CrumbRoute companyRequired title="Reports">
                          <Reports />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="webhooks"
                    element={
                      <ProtectedRoute segment="integrations" minLevel="operator">
                        <CrumbRoute companyRequired title="Webhooks" path="/company/webhooks">
                          <ListWebhookPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  >
                    <Route path=":id" element={<CrumbRoute companyRequired title="Webhook Events" />}>
                      <Route path="events" element={<WebhookEventsPage />} />
                    </Route>
                  </Route>

                  <Route
                    path="packing-slip"
                    element={
                      <ProtectedRoute segment="settings" minLevel="operator">
                        <CrumbRoute companyRequired title="Packing Slip">
                          <PackingSlip />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="packing-slip/*" element={<NotFoundPage />} />

                  <Route
                    path="shipping-methods"
                    element={
                      <ProtectedRoute segment="settings" minLevel="viewer">
                        <CrumbRoute companyRequired title="Shipping Methods">
                          <ShippingMethods />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="email-notifications"
                    element={
                      <ProtectedRoute segment="settings" minLevel="operator">
                        <CrumbRoute companyRequired title="Email Notifications">
                          <EmailNotificationList />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="email-notifications/*" element={<NotFoundPage />} />

                  <Route
                    path="settings"
                    element={
                      <ProtectedRoute segment="settings" minLevel="viewer">
                        <CrumbRoute companyRequired title="Company Settings">
                          <Company />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route path="profile" element={<CrumbRoute title="Profile" />}>
                  <Route path="" element={<Profile />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="integration">
                  <Route path="shopify" element={<Navigate to="shop" />} />
                  <Route
                    path="loop"
                    element={
                      <ProtectedRoute segment="integrations" minLevel="admin">
                        <CrumbRoute companyRequired title="Loop Integration">
                          <LoopIntegrationPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="quickbooks"
                    element={
                      <ProtectedRoute segment="integrations" minLevel="admin">
                        <CrumbRoute title="QuickBooks Integration">
                          <QuickbooksIntegrationPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="shop"
                    element={
                      <ProtectedRoute segment="integrations" minLevel="admin">
                        <CrumbRoute companyRequired title="Shopify Integration">
                          <ShopifyIntegrationPage />
                        </CrumbRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="notification" element={<CrumbRoute title="Notifications" />}>
                  <Route path="" element={<NotificationPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="user" element={<CrumbRoute title="User" />}>
                  <Route path="profile" element={<CrumbRoute title="Profile" />}>
                    <Route path="" element={<Profile />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route
                  path="edi"
                  element={
                    <ProtectedRoute segment="integrations" minLevel="viewer">
                      <CrumbRoute title="EDI">
                        <EDITransactionList />
                      </CrumbRoute>
                    </ProtectedRoute>
                  }
                />
                <Route path="edi/*" element={<NotFoundPage />} />

                <Route
                  path="project"
                  element={
                    <ProtectedRoute segment="vas" minLevel="viewer">
                      <CrumbRoute title="Projects" path="/project" />
                    </ProtectedRoute>
                  }
                >
                  <Route path="" element={<ProjectList />} />
                  <Route path=":id" element={<CrumbRoute title="Project" />}>
                    <Route path="" element={<Project />} />
                  </Route>
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Chrome>
          }
        />
      ) : (
        <Route
          path="/*"
          element={
            <PostPage>
              <Background>
                <Border>
                  <Routes>
                    <Route path="/billing/pricing/" element={<PricingTool />} />
                    <Route
                      path="*"
                      element={
                        <Navigate
                          to={{
                            pathname: '/login',
                            search: `?${searchParams}`,
                          }}
                          replace
                        />
                      }
                    />
                  </Routes>
                </Border>
              </Background>
            </PostPage>
          }
        />
      )}
    </Routes>
  )
}

export const App = () => {
  return (
    <StateProvider>
      <KaseTheme>
        <KaseToastContainer />
        <BreadcrumbsProvider>
          <Router>
            <MainRoutes />
          </Router>
        </BreadcrumbsProvider>
      </KaseTheme>
    </StateProvider>
  )
}

export default App
